import { Analytics } from '@genoa/analytics'
import { useQueryError } from '@genoa/state-management'

import { useAuthState } from '../../../contexts'
import { resetCustomerCardAction, setCustomerCardAction } from '../../../modules/flex2/customer-wallet'
import { useCustomerWalletState } from '../../../modules/flex2/use-state'
import { useLazyGetCardsQuery } from '../../../modules/flexApi'
import { useAnalytics, useLogger } from '../../../providers'
import { useReduxAction } from '../../use-redux-action'

type UseGetCardsParams = {
  componentName: string
  setCardNetwork?: (network: string) => void
  setCardLast4Digits?: (last4OfCard: string) => void
  setCardType?: (cardType: string) => void
  capitalizeCardType?: boolean
}

export const useGetCards = (params: UseGetCardsParams) => {
  const analytics = useAnalytics()
  const componentName = `useGetCardsV2 - ${params.componentName}`
  const loggerV2 = useLogger(componentName)
  const { uid } = useAuthState()
  const setWalletState = useReduxAction(setCustomerCardAction)

  const customerWalletState = useCustomerWalletState()
  const resetCustomerCard = useReduxAction<void>(resetCustomerCardAction)
  const [getCards, { isLoading: loadingDefaultCard, error: getCardsError }] = useLazyGetCardsQuery()

  const getDefaultCard = async () => {
    if (!uid) {
      loggerV2.error('getDefaultCard', 'getDefaultCard called without a customerId')
      return
    }

    const response = await getCards({ customerId: uid! }).unwrap()

    if (response?.ok) {
      analytics.logEvent(Analytics.Events.GET_CARDS_SUCCESS)
      setWalletState({ defaultCard: response?.data.default })
      return response?.data
    }
  }

  useQueryError(getCardsError, {
    onAllErrors() {
      analytics.logEvent(Analytics.Events.GET_CARDS_FAILURE)
      loggerV2.error('getDefaultCardV2', 'Unexpected error')
      resetCustomerCard()
    },
  })

  const updateWalletState = async () => {
    if (!customerWalletState.initialized) {
      await getDefaultCard()
    }
    const defaultCard = customerWalletState.defaultCard
    params.setCardNetwork && params.setCardNetwork(defaultCard.brand)
    params.setCardLast4Digits && params.setCardLast4Digits(defaultCard.card_mask)

    if (params.capitalizeCardType && params.setCardType) {
      const tempCardType =
        defaultCard.card_type && defaultCard.card_type.charAt(0).toUpperCase() + defaultCard.card_type.slice(1)
      tempCardType && params.setCardType(tempCardType)
    } else {
      defaultCard.card_type && params.setCardType && params.setCardType(defaultCard.card_type)
    }
  }

  return {
    getDefaultCard,
    loadingDefaultCard,
    updateWalletState,
  }
}
